import { Inject, Injectable, Optional } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, delay, finalize, map } from 'rxjs/operators';
import { PeopleSdk } from 'lib-klab-sdks';
import { AppEventsService } from '../../events/products-events.service';
import { CoreUIToastDirection, CoreUIToastV2Service } from '../../services/toast.service';
import { EmployeeConfigurationsPort } from '../../../app/ports/people/employee-configurations.port';
import { EnvMicroPeople } from '../../config/env.micro.people';
import { PEOPLE_ENV } from '../../config/micros-env.tokens';
import { employeeConfigurationsSdkMock } from '../../mocks/people/employee-configurations.mock';

type EmployeeConfigurationsMockAction = 'getAll' | 'getById' | 'update';

@Injectable({ providedIn: 'root' })
export class EmployeeConfigurationsPortAdapter implements EmployeeConfigurationsPort {
  constructor(
    private employeeConfigurationsApi: PeopleSdk.EmployeeConfigurationsService,
    private appEvents: AppEventsService,
    @Optional() private toast: CoreUIToastV2Service,
    @Inject(PEOPLE_ENV) private env: EnvMicroPeople,
  ) {}

  getAllEmployeeConfigurations(
    page?: number,
    size?: number,
    locationId?: number,
    workModality?: PeopleSdk.WorkModalityEnum,
    roleId?: number,
  ): Observable<PeopleSdk.PagedEmployeeConfigurationResponse> {
    this.appEvents.loaderVisiblePublish(true, 'people-configurations-list');

    const source$ = this.shouldMock('getAll')
      ? of(employeeConfigurationsSdkMock.getAllEmployeeConfigurations(page, size, locationId, workModality, roleId)).pipe(delay(360))
      : this.employeeConfigurationsApi.getAllEmployeeConfigurations(page, size, locationId, workModality, roleId);

    return source$.pipe(
      map((resp) => resp),
      catchError((err) => {
        this.showErrorToast(err);
        return throwError(() => err);
      }),
      finalize(() => this.appEvents.loaderVisiblePublish(false, 'people-configurations-list')),
    );
  }

  getEmployeeConfigurationById(
    employeeConfigurationId: number,
  ): Observable<PeopleSdk.EmployeeConfigurationByIdResponse> {
    this.appEvents.loaderVisiblePublish(true, 'people-configuration-getById');

    const source$ = this.shouldMock('getById')
      ? of(employeeConfigurationsSdkMock.getEmployeeConfigurationById(employeeConfigurationId)).pipe(delay(360))
      : this.employeeConfigurationsApi.getEmployeeConfigurationById(employeeConfigurationId);

    return source$.pipe(
      map((resp) => resp),
      catchError((err) => {
        this.showErrorToast(err);
        return throwError(() => err);
      }),
      finalize(() => this.appEvents.loaderVisiblePublish(false, 'people-configuration-getById')),
    );
  }

  updateEmployeeConfiguration(
    employeeConfigurationId: number,
    payload: PeopleSdk.EmployeeConfigurationUpdateRequest,
  ): Observable<void> {
    this.appEvents.loaderVisiblePublish(true, 'people-configuration-update');

    const source$ = this.shouldMock('update')
      ? of(employeeConfigurationsSdkMock.updateEmployeeConfiguration(employeeConfigurationId, payload)).pipe(delay(360))
      : this.employeeConfigurationsApi.updateEmployeeConfiguration(employeeConfigurationId, payload);

    return source$.pipe(
      map(() => void 0),
      catchError((err) => {
        this.showErrorToast(err);
        return throwError(() => err);
      }),
      finalize(() => this.appEvents.loaderVisiblePublish(false, 'people-configuration-update')),
    );
  }

  getEmployeeConfiguration(
    employeeId?: number,
    employeeEmail?: string,
  ): Observable<PeopleSdk.EmployeeConfigurationInternalResponse> {
    this.appEvents.loaderVisiblePublish(true, 'people-configuration-internal');

    const source$ = this.shouldMock('getAll')
      ? of(employeeConfigurationsSdkMock.getEmployeeConfiguration(employeeId, employeeEmail)).pipe(delay(360))
      : this.employeeConfigurationsApi.getEmployeeConfiguration(employeeId, employeeEmail);

    return source$.pipe(
      map((resp) => resp),
      catchError((err) => {
        this.showErrorToast(err);
        return throwError(() => err);
      }),
      finalize(() => this.appEvents.loaderVisiblePublish(false, 'people-configuration-internal')),
    );
  }

  exportConfigurations(
    payload: PeopleSdk.EmployeeConfigurationExportRequest,
  ): Observable<Blob> {
    this.appEvents.loaderVisiblePublish(true, 'people-configurations-export');

    return this.employeeConfigurationsApi.exportEmployeeConfigurations(payload).pipe(
      map((resp) => resp),
      catchError((err) => {
        this.showErrorToast(err);
        return throwError(() => err);
      }),
      finalize(() => this.appEvents.loaderVisiblePublish(false, 'people-configurations-export')),
    );
  }

  exportEmployeeConfigurationDetail(
    employeeConfigurationId: number,
    payload: { format: string },
  ): Observable<Blob> {
    this.appEvents.loaderVisiblePublish(true, 'people-configuration-detail-export');

    return this.employeeConfigurationsApi
      .exportEmployeeConfigurationDetail(employeeConfigurationId, payload)
      .pipe(
        map((resp) => resp),
        catchError((err) => {
          this.showErrorToast(err);
          return throwError(() => err);
        }),
        finalize(() =>
          this.appEvents.loaderVisiblePublish(false, 'people-configuration-detail-export'),
        ),
      );
  }

  private shouldMock(action: EmployeeConfigurationsMockAction): boolean {
    return Boolean(this.env?.useMocks || this.env?.mock?.employeeConfigurations?.[action]);
  }

  private showErrorToast(err: any) {
    if (!this.toast) return;

    const status = err?.status;
    const mapMsg: Record<number, string> = {
      400: 'Solicitud inválida',
      401: 'No autorizado',
      404: 'No encontrado',
      429: 'Demasiadas solicitudes',
      500: 'Error interno del servidor',
    };

    this.toast.show({
      data: {
        title: 'Error',
        msg: mapMsg[status] || 'Error inesperado',
        direction: CoreUIToastDirection.topCenter,
        autoClose: true,
      },
    });
  }
}
